/**
 * 채널 × 계정 → **그 채널의 프로필 주소**.
 *
 * 🔑 계정 아이디를 화면에 적는 자리는 여럿이다(인플루언서 상세·크리에이터 허브·계정 줄) — 주소를
 *   만드는 규칙은 **여기 하나**다. 채널마다 경로가 다르고(`@` 를 붙이는 곳·안 붙이는 곳), 두 벌이
 *   되면 한쪽만 새 채널을 알게 된다.
 * ⚠️ **모르는 채널은 빈 문자열**이다 — 부르는 쪽이 링크를 아예 걸지 않는다. 짐작해서 주소를 만들면
 *   404 로 보낸다.
 * ⚠️ 계정 자리에 **주소가 그대로 들어 있으면** 그것을 쓴다(발굴 문서에 섞여 들어온다).
 */
export function toChannelProfileUrl(channel: unknown, account: unknown): string {
  const safeChannel = String(channel ?? '').trim().toLowerCase();
  const rawAccount = String(account ?? '').trim();
  if (!safeChannel || !rawAccount) return '';

  if (/^https?:\/\//i.test(rawAccount)) return rawAccount;

  const handle = rawAccount.replace(/^@+/, '');
  if (!handle) return '';

  if (safeChannel === 'instagram' || safeChannel === 'ig') return `https://www.instagram.com/${handle}`;
  if (safeChannel === 'tiktok') return `https://www.tiktok.com/@${handle}`;
  // ⚠️ 유튜브만 **핸들에 `@` 가 붙은 채로** 저장돼 오는 일이 있다 — 두 번 붙이지 않는다
  if (safeChannel === 'youtube' || safeChannel === 'yt') return `https://www.youtube.com/@${handle}`;
  if (safeChannel === 'x' || safeChannel === 'twitter') return `https://x.com/${handle}`;
  if (safeChannel === 'threads') return `https://www.threads.net/@${handle}`;
  return '';
}

/**
 * 프로필 주소 → **채널 × 계정** — `toChannelProfileUrl` 의 거꾸로.
 *
 * 🔑 계정을 주소로 받는 자리(시작하기의 지켜볼 대상 칸)가 쓴다 — 주소 규칙은 이 파일 하나다.
 * ⚠️ 글·릴스·탐색 같은 **계정이 아닌 경로는 `null`** 이다(`/p/…` · `/reel/…` · `/explore` …) — 짐작해서 계정으로 읽지 않는다.
 * @returns 모르는 주소·계정 아닌 경로면 `null`
 */
export function parseChannelProfileUrl(text: unknown): { channel: string; account: string } | null {
  const raw = String(text ?? '').trim();
  if (!raw) return null;
  let url: URL;
  try {
    url = new URL(/^https?:\/\//i.test(raw) ? raw : `https://${raw}`);
  } catch {
    return null;
  }
  const host = url.hostname.toLowerCase().replace(/^(www\.|m\.|mobile\.)/, '');
  const first = decodeURIComponent(url.pathname.split('/').filter(Boolean)[0] ?? '');
  const channel = PROFILE_HOSTS[host];
  if (!channel || !first) return null;
  const atPath = first.startsWith('@');
  /* 틱톡·유튜브·스레드는 `@아이디` 경로만 계정이다 — 다른 경로는 글·채널 id 다 */
  if ((channel === 'tiktok' || channel === 'youtube' || channel === 'threads') && !atPath) return null;
  const account = first.replace(/^@+/, '').trim();
  if (!account || NON_PROFILE_PATHS.has(account.toLowerCase())) return null;
  return { channel, account };
}

/** 프로필 주소의 호스트 → 채널 — `TRACK_ACCOUNT_PLATFORMS` 의 채널 값과 같은 말 */
const PROFILE_HOSTS: Record<string, string> = {
  'instagram.com': 'instagram',
  'tiktok.com': 'tiktok',
  'youtube.com': 'youtube',
  'x.com': 'x',
  'twitter.com': 'x',
  'threads.net': 'threads',
  'threads.com': 'threads',
  'blog.naver.com': 'naverblog',
};

/** 계정 자리에 오지만 계정이 아닌 첫 경로 */
const NON_PROFILE_PATHS = new Set([
  'p', 'reel', 'reels', 'tv', 'explore', 'stories', 'accounts', 'direct', 'watch', 'shorts', 'channel', 'c',
  'results', 'search', 'home', 'hashtag', 'i', 'intent', 'share', 'login', 'postview.naver', 'postlist.naver',
]);
